// Presentation helpers for the Finance Access panel.
//
// Everything here is UX. The backend decides what is effective, what scope
// resolves, and what the viewer may grant; these helpers only describe the
// view model returned by GET /organisation/users/:id/finance-access and build
// payloads for the existing override endpoints, which re-check everything.

export const SOURCE_KINDS = {
  LEGACY_ROLE: { label: "Role", tone: "slate", explanation: "Comes from the person's role. Change the role to change it." },
  POSITION: { label: "Position default", tone: "indigo", explanation: "Inherited from their position. Change it in Organisation → Positions, or block it for this person." },
  ASSIGNMENT: { label: "Appointment grant", tone: "indigo", explanation: "Granted with this specific appointment. Change it in Organisation." },
  DELEGATION: { label: "Delegation", tone: "sky", explanation: "Temporarily delegated. Revoke it in Organisation → Delegations." },
  DIRECT_OVERRIDE: { label: "Individual exception", tone: "amber", explanation: "Granted to this person individually. It can be revoked here." },
  DIRECT_DENY: { label: "Blocked", tone: "rose", explanation: "Blocked for this person, overriding every other source. Revoke the block to restore it." },
};

// assigned_students resolves exactly like assigned_class in the backend; one
// choice avoids implying a difference that does not exist.
const HIDDEN_SCOPES = new Set(["assigned_students"]);

export const SCOPE_ANCHORS = {
  assigned_unit: "unit",
  selected_classes: "classes",
  selected_programmes: "programmes",
};

export const rowStatus = (row) => {
  if (row.denied) return { key: "blocked", label: "Blocked for this person" };
  if (!row.effective) return { key: "not_granted", label: "Not granted" };
  if (row.resolved_scope?.self_service_only) return { key: "self_service", label: "Self-service only" };
  if (row.resolved_scope?.empty) return { key: "no_scope", label: "Held, but no usable scope" };
  return { key: "allowed", label: "Allowed" };
};

export const scopeSummary = (resolved) => {
  if (!resolved || resolved.empty) return "No access (nothing resolves)";
  if (resolved.self_service_only) return "Own record or linked children only";
  if (resolved.whole_school) return "Whole school";
  const parts = [];
  if (resolved.class_count) parts.push(`${resolved.class_count} class${resolved.class_count === 1 ? "" : "es"}`);
  if (resolved.programme_count) parts.push(`${resolved.programme_count} programme${resolved.programme_count === 1 ? "" : "s"}`);
  if (resolved.teaching_placements) parts.push("classes they teach");
  return parts.join(" + ");
};

export const removableSource = (source) =>
  Boolean(source.override_id) && (source.source_type === "DIRECT_OVERRIDE" || source.source_type === "DIRECT_DENY");

// Scopes offered for a set of capabilities: only those the backend says the
// viewer may grant for every selected capability.
export const grantableScopes = (rows, codes, scopeLabels = {}) => {
  const selected = rows.filter((row) => codes.includes(row.code));
  if (!selected.length || selected.some((row) => !row.grant?.allowed)) return [];
  const [first, ...rest] = selected;
  return first.grant.scopes
    .filter((scope) => !HIDDEN_SCOPES.has(scope) && rest.every((row) => row.grant.scopes.includes(scope)))
    .map((scope) => ({ code: scope, label: scopeLabels[scope] || scope, anchor: SCOPE_ANCHORS[scope] || null }));
};

export const localToIso = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const emptyGrant = () => ({
  codes: [], scope: "", unitId: "", classIds: [], programIds: [],
  duration: "until_revoked", startsAt: "", expiresAt: "", reason: "",
});

export const validateGrant = (form, { now = new Date(), units = [] } = {}) => {
  const errors = [];
  if (!form.codes.length) errors.push("Choose at least one permission.");
  if (!form.scope) errors.push("Choose where this applies.");
  const anchor = SCOPE_ANCHORS[form.scope];
  if (anchor === "unit") {
    if (!form.unitId) errors.push("Choose the organisational unit.");
    else if (units.find((unit) => unit.id === form.unitId)?.resolves === false) errors.push("That unit has no linked programmes or classes yet, so the grant would give no access. Configure the unit first.");
  }
  if (anchor === "classes" && !form.classIds.length) errors.push("Choose at least one class.");
  if (anchor === "programmes" && !form.programIds.length) errors.push("Choose at least one programme.");
  if (form.duration === "temporary") {
    const start = localToIso(form.startsAt);
    const end = localToIso(form.expiresAt);
    if (!end) errors.push("Temporary access needs an end date.");
    if (form.startsAt && !start) errors.push("The start date is not valid.");
    if (end && new Date(end) <= now) errors.push("The end date must be in the future.");
    if (start && end && new Date(start) >= new Date(end)) errors.push("The start must be before the end.");
  }
  if (form.reason.trim().length < 5) errors.push("Record a reason (at least 5 characters) for the audit trail.");
  return errors;
};

// One payload per capability for POST /organisation/users/:id/overrides.
export const buildGrantPayloads = (form) => {
  const anchor = SCOPE_ANCHORS[form.scope];
  const temporary = form.duration === "temporary";
  return form.codes.map((capability) => ({
    capability,
    effect: "grant",
    scope: form.scope,
    reason: form.reason.trim(),
    starts_at: temporary ? localToIso(form.startsAt) : null,
    expires_at: temporary ? localToIso(form.expiresAt) : null,
    ...(anchor === "unit" ? { unit_id: form.unitId } : {}),
    ...(anchor === "classes" ? { class_ids: form.classIds } : {}),
    ...(anchor === "programmes" ? { program_ids: form.programIds } : {}),
  }));
};

// A deny removes the capability everywhere; the backend requires a valid
// scope string and no anchors, so it is recorded as institution-wide.
export const denyPayload = (capability, reason) => ({ capability, effect: "deny", scope: "institution_all", reason: reason.trim() });

export const HISTORY_LABELS = { active: "Active", scheduled: "Scheduled", expired: "Expired", revoked: "Revoked" };

export const formatInstant = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("en-NG", { dateStyle: "medium", timeStyle: "short" });
};

export const durationLabel = (item) => {
  if (item.starts_at && item.expires_at) return `${formatInstant(item.starts_at)} → ${formatInstant(item.expires_at)}`;
  if (item.expires_at) return `Until ${formatInstant(item.expires_at)}`;
  if (item.starts_at) return `From ${formatInstant(item.starts_at)}`;
  return "Until revoked";
};
